import axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";
import {
  getAccessToken as getLocalAccessToken,
  getRefreshToken,
  getUser,
} from "../hooks/user.actions";
import { getAccessToken as getOktaAccessToken, getTokenWhenReady, setAccessToken, clearAccessToken } from "./tokenStore";
import { oktaAuth } from '../pages/Login';

import {variables} from '../Variables.js';

/**
 * Clear all auth tokens (local storage + token store) and redirect to login.
 * Used when token is expired or refresh fails.
 */
function clearAllTokensAndRedirect() {
  clearAccessToken();
  localStorage.clear();
  // Use replace (full page reload) so the Okta SDK in-memory session is
  // destroyed — a hash-only change keeps the SDK alive and auto-re-authenticates.
  window.location.replace(window.location.origin + "/#/login/");
}

const axiosService = axios.create({
  baseURL: variables.API_URL + 'api',
  headers: {
    "Content-Type": "application/json",
  },
});

axiosService.interceptors.request.use(async (config) => {
  /**
   * Retrieving the access token from Okta token store (preferred)
   * Falls back to local storage token for backward compatibility.
   * On full page refresh, Okta token may not be set yet (AuthTokenSync runs in useEffect).
   * Wait briefly for the token to be set so the first requests don't get 403.
   */
  let oktaToken = getOktaAccessToken();
  if (!oktaToken) {
    try {
      oktaToken = await Promise.race([
        getTokenWhenReady(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Auth timeout")), 3000)
        ),
      ]);
    } catch {
      oktaToken = null;
    }
  }

  // Try to get local token, but handle case where it might be null
  let localToken = null;
  try {
    localToken = getLocalAccessToken();
  } catch (error) {
    // If getLocalAccessToken throws an error (e.g., auth is null), ignore it
    localToken = null;
  }

  const token = oktaToken || localToken;

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});

axiosService.interceptors.response.use(
  (res) => Promise.resolve(res),
  (err) => Promise.reject(err)
);

const refreshAuthLogic = async (failedRequest) => {
  // Try Okta token renewal first (primary auth method).
  try {
    const freshToken = await oktaAuth.getOrRenewAccessToken();
    if (freshToken) {
      setAccessToken(freshToken);
      failedRequest.response.config.headers["Authorization"] = "Bearer " + freshToken;
      return Promise.resolve();
    }
  } catch {
    // Okta renewal failed — fall through to SimpleJWT path.
  }

  // Fallback: SimpleJWT refresh for local-login users.
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearAllTokensAndRedirect();
    return Promise.reject();
  }

  return axios
    .post(
      "/auth/refresh/",
      { refresh: refreshToken },
      { baseURL: variables.API_URL + 'api' }
    )
    .then((resp) => {
      const { access } = resp.data;
      failedRequest.response.config.headers["Authorization"] = "Bearer " + access;
      localStorage.setItem(
        "auth",
        JSON.stringify({ access, refresh: refreshToken, user: getUser() })
      );
    })
    .catch(() => {
      clearAllTokensAndRedirect();
      return Promise.reject();
    });
};

createAuthRefreshInterceptor(axiosService, refreshAuthLogic);

export function fetcher(url) {
  return axiosService.get(url).then((res) => res.data);
}

export default axiosService;